// collections.js

// frontend/app/collections.js
import axios from "axios";
import { FASTAPI_BASE_URL } from "./config";

// ---------- helpers ----------
async function handle(res) {
  let data = null, text = null;
  try { data = await res.json(); } catch { text = await res.text(); }
  const errMsg =
    data?.message ||
    data?.detail?.message ||
    data?.detail ||
    data?.error ||
    text ||
    "Request failed.";
  if (!res.ok) throw new Error(errMsg);
  return data;
}

// ---------- constants ----------
const API_COLLECTIONS = `${FASTAPI_BASE_URL}/collections`;

// ---------- endpoints ----------

// Create a new (empty) collection
export async function apiCreateCollection({ name, base_board, owner_email, strict_mode = true, description = "" }) {
  const res = await fetch(API_COLLECTIONS, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      name,
      base_board,
      owner_email,
      strict_mode,
      description,
      status: "draft",
      visibility_scope: "private",
      version: 1,
      items: []
    })
  });
  return handle(res);
}

// export async function apiRenameCollection(id, name, actingTeacherEmail) {
//   const res = await axios.patch(
//     `${API_COLLECTIONS}/${id}?acting_teacher_email=${encodeURIComponent(actingTeacherEmail)}`,
//     { name }
//   );
//   return res.data;
// }

// export async function apiDeleteCollection(id, actingTeacherEmail) {
//   await axios.delete(
//     `${API_COLLECTIONS}/${id}?acting_teacher_email=${encodeURIComponent(actingTeacherEmail)}`
//   );
//   // FastAPI returns 204 No Content on success
//   if (res.status !== 204) throw new Error("Delete failed");
//   return true;
// }

export async function apiRenameCollection(id, name, teacherEmail) {
  const url = `${API_COLLECTIONS}/${id}?acting_teacher_email=${encodeURIComponent(teacherEmail)}`;
  const res = await axios.patch(url, { name });
  return res.data;
}

// export async function apiDeleteCollection(id, teacherEmail) {
//   const url = `${API_COLLECTIONS}/${id}?acting_teacher_email=${encodeURIComponent(teacherEmail)}`;
//   const res = await axios.delete(url);
//   // 204 No Content → res.data is undefined, return a simple ok object
//   return res?.data ?? { ok: true };
// }
export async function apiDeleteCollection(id, actingTeacherEmail) {
  const res = await axios.delete(
    `${API_COLLECTIONS}/${id}`,
    { params: { acting_teacher_email: actingTeacherEmail } }
  );
  // FastAPI should return 204 No Content
  if (res.status !== 204) throw new Error("Delete failed");
  return true;
}


// List collections (by board, with optional owner/visibility)
export async function apiListCollections({ board, owner_email, visibility }) {
  const url = new URL(API_COLLECTIONS);
  url.searchParams.set("board", board);
  if (owner_email) url.searchParams.set("owner_email", owner_email);
  if (visibility)  url.searchParams.set("visibility", visibility);
  const res = await fetch(url.toString(), { method: "GET" });
  return handle(res);
}


// Get a single collection (raw)
export async function apiGetCollection(collection_id) {
  const res = await fetch(`${API_COLLECTIONS}/${collection_id}`, { method: "GET" });
  return handle(res);
}

// Build a template from board topic (videos, PWOs, quizzes snapshot)
export async function apiTemplateFromTopic({ board, board_topic_id, source_topic_id, language_id = 1 }) {
  const url = new URL(`${API_COLLECTIONS}/template_from_topic`);
  url.searchParams.set("board", board);
  url.searchParams.set("board_topic_id", String(board_topic_id));
  url.searchParams.set("source_topic_id", String(source_topic_id));
  url.searchParams.set("language_id", String(language_id));
  const res = await fetch(url.toString(), { method: "GET" });
  return handle(res);
}

// Clone directly into a collection from a board topic
export async function apiCloneFromTopic({ collection_id, board, board_topic_id, source_topic_id, language_id = 1, acting_teacher_email, module_title, section }) {
  const url = new URL(`${API_COLLECTIONS}/${collection_id}/items/clone_from_topic`);
  url.searchParams.set("board", board);
  url.searchParams.set("board_topic_id", String(board_topic_id));
  url.searchParams.set("source_topic_id", String(source_topic_id));
  url.searchParams.set("language_id", String(language_id));
  url.searchParams.set("acting_teacher_email", acting_teacher_email);
  if (module_title) url.searchParams.set("module_title", module_title);
  if (section)      url.searchParams.set("section", section);

  const res = await fetch(url.toString(), { method: "POST" });
  return handle(res);
}

// BDD 04/28/2026
// // Patch an item's selections (keep only these)
// export async function apiPatchItemSelections({ item_id, acting_teacher_email, videos_keep_tag_ids, pwos_keep_scene_names, quiz_keep_question_ids }) {
//   const url = new URL(`${API_COLLECTIONS}/items/${item_id}/selections`);
//   url.searchParams.set("acting_teacher_email", acting_teacher_email);
//   const res = await fetch(url.toString(), {
//     method: "PATCH",
//     headers: { "Content-Type": "application/json" },
//     body: JSON.stringify({ videos_keep_tag_ids, pwos_keep_scene_names, quiz_keep_question_ids })
//   });
//   return handle(res);
// }

// BDD 04/28/2026
export async function apiPatchItemSelections({
  item_id,
  acting_teacher_email,
  videos_keep_tag_ids,
  videos_keep_video_ids_no_tag,
  pwos_keep_scene_names,
  quiz_keep_question_ids
}) {
  const url = new URL(`${API_COLLECTIONS}/items/${item_id}/selections`);
  url.searchParams.set("acting_teacher_email", acting_teacher_email);
  const res = await fetch(url.toString(), {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      videos_keep_tag_ids,
      videos_keep_video_ids_no_tag,
      pwos_keep_scene_names,
      quiz_keep_question_ids
    })
  });
  return handle(res);
}

// Patch an item's labels/order/etc.
export async function apiPatchItem({ item_id, acting_teacher_email, patch }) {
  const url = new URL(`${API_COLLECTIONS}/items/${item_id}`);
  url.searchParams.set("acting_teacher_email", acting_teacher_email);
  const res = await fetch(url.toString(), {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch || {})
  });
  return handle(res);
}

// Reorder items in a collection
export async function apiReorderItems({ collection_id, acting_teacher_email, item_ids_in_order }) {
  const url = new URL(`${API_COLLECTIONS}/${collection_id}/reorder`);
  url.searchParams.set("acting_teacher_email", acting_teacher_email);
  const res = await fetch(url.toString(), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ item_ids_in_order })
  });
  if (!res.ok) {
    let text = await res.text();
    throw new Error(text || "Failed to reorder");
  }
  return true;
}

// // Get a resolved collection (render-ready)
// export async function apiGetCollectionResolved(collection_id) {
//   const res = await fetch(`${API_COLLECTIONS}/${collection_id}/resolved`, { method: "GET" });
//   return handle(res);
// }
// BDD 10/3/2025
// Get a resolved collection (render-ready) with optional query params
export async function apiGetCollectionResolved(collection_id, params = {}) {
  // Build a safe query string (RN-safe too)
  const qp = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null && v !== "")
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
    .join("&");

  const url = `${API_COLLECTIONS}/${collection_id}/resolved${qp ? `?${qp}` : ""}`;
  const res = await fetch(url, { method: "GET" });
  return handle(res);
}

// PUBLISH / UNPUBLISH / ARCHIVE
// export async function apiPublishCollection(collectionId, visibilityScope, teacherEmail) {
//   const body = visibilityScope ? { visibility_scope: visibilityScope } : {};
//   const url  = `collections/${collectionId}/publish?acting_teacher_email=${encodeURIComponent(teacherEmail)}`;
//   const res  = await axios.post(url, body);
//   return res.data;
// }

// export async function apiUnpublishCollection(collectionId, teacherEmail) {
//   const url = `collections/${collectionId}/unpublish?acting_teacher_email=${encodeURIComponent(teacherEmail)}`;
//   const res = await axios.post(url, {});
//   return res.data;
// }

// export async function apiArchiveCollection(collectionId, teacherEmail) {
//   const url = `collections/${collectionId}/archive?acting_teacher_email=${encodeURIComponent(teacherEmail)}`;
//   const res = await axios.post(url, {});
//   return res.data;
// }
// BDD 10/6/2025

// PUBLISH / UNPUBLISH / ARCHIVE
export async function apiPublishCollection(collectionId, visibilityScope, teacherEmail) {
  const body = visibilityScope ? { visibility_scope: visibilityScope } : {};
  const url  = `collections/${collectionId}/publish?acting_teacher_email=${encodeURIComponent(teacherEmail)}`;

  console.log("[VIS][apiPublish] →", { url, body, collectionId, visibilityScope, teacherEmail, baseURL: axios.defaults?.baseURL });

  try {
    const res = await axios.post(url, body);
    console.log("[VIS][apiPublish] ←", res.status, res.data);
    return res.data;
  } catch (err) {
    console.log("[VIS][apiPublish][ERR]", err?.response?.status, err?.response?.data || err.message);
    throw err;
  }
}

export async function apiUnpublishCollection(collectionId, teacherEmail) {
  const url = `collections/${collectionId}/unpublish?acting_teacher_email=${encodeURIComponent(teacherEmail)}`;

  console.log("[VIS][apiUnpublish] →", { url, collectionId, teacherEmail, baseURL: axios.defaults?.baseURL });

  try {
    const res = await axios.post(url, {});
    console.log("[VIS][apiUnpublish] ←", res.status, res.data);
    return res.data;
  } catch (err) {
    console.log("[VIS][apiUnpublish][ERR]", err?.response?.status, err?.response?.data || err.message);
    throw err;
  }
}

export async function apiArchiveCollection(collectionId, teacherEmail) {
  const url = `collections/${collectionId}/archive?acting_teacher_email=${encodeURIComponent(teacherEmail)}`;

  console.log("[VIS][apiArchive] →", { url, collectionId, teacherEmail, baseURL: axios.defaults?.baseURL });

  try {
    const res = await axios.post(url, {});
    console.log("[VIS][apiArchive] ←", res.status, res.data);
    return res.data;
  } catch (err) {
    console.log("[VIS][apiArchive][ERR]", err?.response?.status, err?.response?.data || err.message);
    throw err;
  }
}



// SHARES
// // --- list current per-student shares
// export async function apiListShares({ collection_id, acting_teacher_email }) {
//   const url = new URL(`${API_COLLECTIONS}/${collection_id}/shares`);
//   url.searchParams.set("acting_teacher_email", acting_teacher_email);
//   const res = await fetch(url.toString(), { method: "GET" });
//   return handle(res); // [{student_email, assigned_by, status, ...}, ...]
// }
// List per-student shares for a collection
export async function apiListShares({ collection_id, acting_teacher_email }) {
  if (!collection_id) throw new Error("collection_id is required");
  const url = new URL(`${API_COLLECTIONS}/${collection_id}/shares`);
  if (acting_teacher_email) url.searchParams.set("acting_teacher_email", acting_teacher_email);
  const res = await fetch(url.toString(), { method: "GET" });
  return handle(res);
}

// --- add shares (comma or newline-separated emails OK; pass normalized array)
export async function apiAddShares({ collection_id, acting_teacher_email, student_emails }) {
  const url = new URL(`${API_COLLECTIONS}/${collection_id}/shares`);
  url.searchParams.set("acting_teacher_email", acting_teacher_email);
  const res = await fetch(url.toString(), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ student_emails })
  });
  return handle(res);
}
// Share this collection with all students on the teacher's email domain
export async function apiAssignSameDomain({ collection_id, acting_teacher_email }) {
  if (!collection_id) throw new Error("collection_id required");
  if (!acting_teacher_email) throw new Error("acting_teacher_email required");

  const url = new URL(`${API_COLLECTIONS}/${collection_id}/shares/assign_same_domain`);
  url.searchParams.set("acting_teacher_email", acting_teacher_email);

  const res = await fetch(url.toString(), { method: "POST" });
  return handle(res);
}


// // Assign shares to all students with same email domain as the teacher
// export async function apiAssignSharesSameDomain({ collection_id, acting_teacher_email }) {
//   if (!collection_id) throw new Error("collection_id is required");
//   if (!acting_teacher_email) throw new Error("acting_teacher_email is required");
//   const url = new URL(`${API_COLLECTIONS}/${collection_id}/shares/assign_same_domain`);
//   url.searchParams.set("acting_teacher_email", acting_teacher_email);
//   const res = await fetch(url.toString(), { method: "POST" });
//   return handle(res);
// }
// --- remove a single student share
export async function apiRemoveShare({ collection_id, acting_teacher_email, student_email }) {
  const url = new URL(`${API_COLLECTIONS}/${collection_id}/shares`);
  url.searchParams.set("acting_teacher_email", acting_teacher_email);
  url.searchParams.set("student_email", student_email);
  const res = await fetch(url.toString(), { method: "DELETE" });
  return handle(res); // {ok:true}
}

// Visible/published collections for a given user (student/teacher)
// GET /api/collections/visible?board=...&user_id=...
export async function apiVisibleCollections({ board, user_id }) {
  const url = new URL(`${API_COLLECTIONS}/visible`);
  url.searchParams.set("board", board);
  if (user_id) url.searchParams.set("user_id", user_id);
  const res = await fetch(url.toString(), { method: "GET" });
  return handle(res);
}

// export async function apiListAvailableCollections(studentEmail, base_board) {
//   const url = new URL(`${FASTAPI_BASE_URL}/collections/available`);
//   url.searchParams.set("student_email", studentEmail);
//   if (base_board) url.searchParams.set("base_board", base_board);
//   const res = await fetch(url.toString());
//   const data = await res.json();
//   if (!res.ok) throw new Error(data?.detail || "Failed listing available collections");
//   return data;
// }
// BDD 11/3/2025
export async function apiListAvailableCollections(studentEmail, base_board) {
  const url = new URL(`${FASTAPI_BASE_URL}/collections/available`);
  url.searchParams.set("student_email", studentEmail);
  if (base_board) url.searchParams.set("base_board", base_board);
  url.searchParams.set("fields", "id,name,base_board,is_active,updated_at,item_count"); // skinny index

  const t0 = performance?.now?.() ?? Date.now();
  const res = await fetch(url.toString());
  const raw = await res.text(); // read once
  const t1 = performance?.now?.() ?? Date.now();

  let data;
  try { data = JSON.parse(raw); } catch { data = null; }

  if (!res.ok) throw new Error((data && data.detail) || "Failed listing available collections");

  const ms = Math.round(t1 - t0);
  const kb = Math.round(raw.length / 1024);
  console.log("[COLL] LIST wire", { ms, kb, count: Array.isArray(data) ? data.length : 0 });

  return data;
}

// ---------- Share Collection with Class ----------

// Fetch teacher's classes (uses axios for auth header)
export async function apiGetTeacherClasses(teacherEmail) {
  const response = await axios.get(
    `${FASTAPI_BASE_URL}/classes/teacher/classes`,
    { params: { teacher_email: teacherEmail, for_sharing: true } }
  );
  return response.data?.classes || [];
}

// Share a collection with a class
export async function apiShareCollectionWithClass(collectionId, classId) {
  const res = await fetch(`${API_COLLECTIONS}/${collectionId}/share_with_class`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ class_id: classId }),
  });
  return handle(res);
}

// List classes that a collection is shared with
export async function apiListCollectionSharedClasses(collectionId) {
  const res = await fetch(`${API_COLLECTIONS}/${collectionId}/shared_classes`, { method: "GET" });
  return handle(res);
}

// Remove a class from collection sharing
export async function apiRemoveCollectionClassShare(collectionId, classId) {
  const res = await fetch(`${API_COLLECTIONS}/${collectionId}/shared_classes/${classId}`, { method: "DELETE" });
  return handle(res);
}

// Delete a single collection item
export async function apiDeleteCollectionItem({ item_id, acting_teacher_email }) {
  const res = await fetch(`${API_COLLECTIONS}/items/${item_id}?acting_teacher_email=${encodeURIComponent(acting_teacher_email)}`, {
    method: "DELETE",
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || "Failed to delete collection item");
  }
  return true;
}

// Remember (or clear) the student's active collection for a board
export async function apiSelectActiveCollection({ user_id, base_board, collection_id }) {
  const res = await fetch(`${API_COLLECTIONS}/select`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ user_id, base_board, collection_id }),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || "Failed to select active collection");
  }
  return true;
}

